import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Alert, Button, Card, IconSwap, cn } from './ui';
import { SearchIcon, SpinnerIcon } from './icons';

interface SearchFormProps {
  onBuscarPorDocumento: (tipoDoc: string, numDoc: string) => void;
  isLoading: boolean;
}

const TIPOS_DOC = ['DNI', 'L.E.', 'Pasaporte', 'Visa', 'L.C.', 'C.I.'];

const fieldLabel = 'mb-1.5 block text-xs font-semibold text-slate-600';

const field =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 ' +
  'transition-colors duration-150 ease-out outline-none ' +
  'focus:border-gappex-primary focus:ring-2 focus:ring-gappex-primary/30';

const SearchForm: React.FC<SearchFormProps> = ({ onBuscarPorDocumento, isLoading }) => {
  const [tipoDoc, setTipoDoc] = useState('DNI');
  const [numDoc, setNumDoc] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!numDoc.trim()) {
      setError('Ingresá un número de documento para buscar.');
      return;
    }
    onBuscarPorDocumento(tipoDoc, numDoc.trim());
  };

  return (
    <Card as="section" className="p-6">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
          <div>
            <label htmlFor="tipoDoc" className={fieldLabel}>
              Tipo de documento
            </label>
            <select
              id="tipoDoc"
              className={field}
              value={tipoDoc}
              onChange={(e) => setTipoDoc(e.target.value)}
            >
              {TIPOS_DOC.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="numDoc" className={fieldLabel}>
              Número de documento
            </label>
            <input
              id="numDoc"
              type="text"
              inputMode="numeric"
              autoFocus
              autoComplete="off"
              className={cn(field, 'tabular-nums')}
              placeholder="Sin puntos ni espacios"
              value={numDoc}
              onChange={(e) => {
                setNumDoc(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          <Button
            variant="primary"
            type="submit"
            disabled={isLoading}
            className="h-[42px] w-full sm:w-auto"
            icon={
              <IconSwap swapKey={isLoading ? 'busy' : 'idle'}>
                {isLoading ? <SpinnerIcon /> : <SearchIcon />}
              </IconSwap>
            }
          >
            {isLoading ? 'Buscando…' : 'Buscar'}
          </Button>
        </div>

        <AnimatePresence initial={false}>
          {error && (
            <Alert key="search-error" tone="error" className="mt-4" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </AnimatePresence>
      </form>
    </Card>
  );
};

export default SearchForm;
